import { detectCountryFromPhoneValue } from "@/app/(public)/share/booking/[orgSlug]/[eventSlug]/public-booking-phone-countries";
import type { Translate } from "@/lib/i18n/translate";
import {
  BRAND_HOME_ARIA_LABEL,
  BRAND_LOGO_ALT,
  BRAND_LOGO_TITLE,
} from "@/lib/marketing-seo";
import { isValidDemoLeadWebsite } from "@v1/backend/logic/demoLeads/domain";
import { isValidBookingPhone } from "@v1/backend/logic/scheduling/validate_phone";
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import { ArrowLeft } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";

/**
 * Design tokens for the Demo-Call page — black and white on white, Inter, the
 * same language as the landing page (`marketing/hero/hero-tokens.ts`).
 */
const INTER_STACK =
  "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif";

export const T = {
  /** Ink carries everything loud: buttons, headlines, the submit action. */
  accent: "#0B0F19",
  paper: "#F4F4F5",
  ink: "#0B0F19",
  inkMute: "rgba(11,15,25,0.6)",
  inkFaint: "rgba(11,15,25,0.5)",
  dark: "#0B0F19",
  /** Headlines — same family, heavier weight at the call site. */
  display: INTER_STACK,
  sans: INTER_STACK,
  /** Uppercase, letter-spaced eyebrows. */
  label: INTER_STACK,
} as const;

export type DemoFormValues = {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  units: number;
};

export type DemoFieldKey = "firstName" | "email" | "website" | "phone";
export type DemoFormErrors = Partial<Record<DemoFieldKey, string>>;

export const EMPTY_VALUES: DemoFormValues = {
  firstName: "",
  lastName: "",
  company: "",
  email: "",
  phone: "",
  website: "",
  units: 800,
};

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function isValidWebsite(value: string): boolean {
  return isValidDemoLeadWebsite(value);
}

export function formatUnits(units: number, localeTag: string): string {
  return units >= 10000 ? "10.000+" : units.toLocaleString(localeTag);
}

/** Validate the form; returns the error map (empty = valid). */
export function validateDemoForm(
  t: Translate,
  values: DemoFormValues,
): DemoFormErrors {
  const errors: DemoFormErrors = {};
  if (values.firstName.trim().length === 0) {
    errors.firstName = t("bitteGibDeinenVornamen");
  }
  if (!isValidEmail(values.email)) {
    errors.email = t("bitteGibGueltigeE");
  }
  if (values.website.trim().length === 0) {
    errors.website = t("bitteGibWebseiteDeiner");
  } else if (isValidEmail(values.website)) {
    errors.website = t("bitteGibWebadresseNicht");
  } else if (!isValidWebsite(values.website)) {
    errors.website = t("bitteGibGueltigeWebadresse");
  }
  const phoneCountry = detectCountryFromPhoneValue(values.phone) ?? "DE";
  if (
    values.phone.trim().length === 0 ||
    !isValidBookingPhone(values.phone, phoneCountry)
  ) {
    errors.phone = t("bitteGueltigeTelefonnummerEingeben");
  }
  return errors;
}

export function BrandLogo({
  size = 30,
}: { readonly size?: number }): ReactNode {
  const t = useTranslations("public.demoCallShared");
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: 11 }}
      aria-label={BRAND_LOGO_ALT}
      title={BRAND_LOGO_TITLE}
    >
      <BrandLogoMark width={size} height={size} variant="dark" />
      <span
        style={{
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          color: T.ink,
          fontFamily: T.sans,
        }}
      >
        {t.rich("hausverwalterAi", {
          suffix: (chunks) => (
            <span style={{ color: "#8A8D94" }}>{chunks}</span>
          ),
        })}
      </span>
    </span>
  );
}

/** Minimal top bar: brand (→ home) + "Zurück" link. */
export function DemoNav({
  compact = false,
}: { readonly compact?: boolean }): ReactNode {
  const t = useTranslations("public.demoCallShared");
  const linkStyle: CSSProperties = { textDecoration: "none" };
  return (
    <nav
      style={{
        maxWidth: 1240,
        margin: "0 auto",
        height: compact ? 58 : 74,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: compact ? "0 18px" : "0 24px",
      }}
    >
      <Link
        href="/"
        style={linkStyle}
        aria-label={BRAND_HOME_ARIA_LABEL}
        title={BRAND_LOGO_TITLE}
      >
        <BrandLogo size={compact ? 28 : 30} />
      </Link>
      <Link
        href="/"
        style={{
          ...linkStyle,
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontSize: compact ? 13.5 : 14,
          fontWeight: 500,
          color: T.inkMute,
          fontFamily: T.sans,
        }}
      >
        <ArrowLeft size={15} strokeWidth={1.9} aria-hidden="true" />
        {t("zurueck")}
      </Link>
    </nav>
  );
}
